import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import styles from "./Search.module.css";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import NavSlider from "../NavSlider/NavSlider";

/* =========================================================
   API CONFIGURATION
========================================================= */

const API_URL =
  "https://coinyoco.world/brobizee-api/get_partnerdata.php";

const API_BASE_URL =
  "https://coinyoco.world/brobizee-api";

/* =========================================================
   DEFAULT PROFILE IMAGE
========================================================= */

const DEFAULT_AVATAR =
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=300&q=80";

/* =========================================================
   HELPER FUNCTIONS
========================================================= */

const safeString = (value, fallback = "") => {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value).trim() || fallback;
};

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const formatExperience = (value) => {
  const experience = safeString(value, "");
  if (!experience) return "Experience not added";
  if (/year|yr|years|yrs/i.test(experience)) return experience;
  return `${experience} Yrs`;
};

const formatAge = (value) => {
  const age = safeString(value, "");
  if (!age) return "Age N/A";
  if (/year|yr|years|yrs/i.test(age)) return age;
  return `${age} Yrs`;
};

const formatPrice = (partner) => {
  const possibleCharges = [
    partner.charges,
    partner.charge,
    partner.price,
    partner.hourly_rate,
    partner.rate,
  ];

  const found = possibleCharges.find(
    (value) =>
      value !== null &&
      value !== undefined &&
      String(value).trim() !== ""
  );

  if (found !== undefined) {
    return `${found}Rs`;
  }

  return "Contact";
};

const formatTeamSize = (partner) => {
  const helperList = safeString(partner.helper_list, "");

  if (!helperList) return "1 Person";

  const numberMatch = helperList.match(/\d+/);

  if (numberMatch) {
    const count = Number(numberMatch[0]);
    if (count > 0) return `${count}+Team`;
  }

  return "Team";
};

const getProfileImage = (profile) => {
  const image = safeString(profile, "");

  if (!image) return DEFAULT_AVATAR;

  if (
    image.startsWith("http://") ||
    image.startsWith("https://") ||
    image.startsWith("data:image/")
  ) {
    return image;
  }

  if (image.startsWith("/")) {
    return `${API_BASE_URL}${image}`;
  }

  return `${API_BASE_URL}/${image}`;
};

/* =========================================================
   LOCATION HELPERS
========================================================= */

const parseLatitudeLongitude = (value) => {
  const location = safeString(value, "");

  if (!location) return null;

  const cleaned = location
    .replace(/[()[\]]/g, "")
    .trim();

  const parts = cleaned.split(",");

  if (parts.length < 2) return null;

  const latitude = Number(parts[0].trim());
  const longitude = Number(parts[1].trim());

  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude)
  ) {
    return null;
  }

  if (latitude < -90 || latitude > 90) return null;
  if (longitude < -180 || longitude > 180) return null;

  return { latitude, longitude };
};

const calculateDistanceKm = (
  latitude1,
  longitude1,
  latitude2,
  longitude2
) => {
  const earthRadiusKm = 6371;

  const toRadians = (degree) =>
    (degree * Math.PI) / 180;

  const dLatitude = toRadians(
    latitude2 - latitude1
  );

  const dLongitude = toRadians(
    longitude2 - longitude1
  );

  const lat1 = toRadians(latitude1);
  const lat2 = toRadians(latitude2);

  const a =
    Math.sin(dLatitude / 2) *
      Math.sin(dLatitude / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(dLongitude / 2) *
      Math.sin(dLongitude / 2);

  const c =
    2 *
    Math.atan2(
      Math.sqrt(a),
      Math.sqrt(1 - a)
    );

  return earthRadiusKm * c;
};

const formatDistance = (distanceKm) => {
  if (
    distanceKm === null ||
    distanceKm === undefined ||
    !Number.isFinite(distanceKm)
  ) {
    return "Distance unavailable";
  }

  if (distanceKm < 1) {
    const meters = Math.round(distanceKm * 1000);
    return `${meters} m away`;
  }

  return `${distanceKm.toFixed(2)} Km away`;
};

/* =========================================================
   FORMAT PARTNER
========================================================= */

const formatPartner = (partner) => {
  const work = safeString(
    partner.work,
    "Service Provider"
  );

  const secondaryWork = safeString(
    partner.secondary_work,
    ""
  );

  const experiences = formatExperience(
    partner.experiences
  );

  const age = formatAge(partner.age);

  const recentlyWork = safeString(
    partner.recently_work,
    ""
  );

  const bio = safeString(partner.bio, "");

  const location = safeString(
    partner.latitude_longitude,
    ""
  );

  const partnerCoordinates =
    parseLatitudeLongitude(location);

  const skillsParts = [];

  if (secondaryWork) skillsParts.push(secondaryWork);
  if (work) skillsParts.push(work);

  if (bio && skillsParts.length === 0) {
    skillsParts.push(bio);
  }

  const skills =
    skillsParts.join(" • ") ||
    "Professional Service";

  const onlineStatus = safeString(
    partner.online_stutes,
    ""
  ).toLowerCase();

  const online =
    onlineStatus === "online" ||
    onlineStatus === "on" ||
    onlineStatus === "active";

  const rating =
    partner.rating !== undefined &&
    partner.rating !== null
      ? safeString(partner.rating, "0.0")
      : "0.0";

  const views =
    partner.views !== undefined &&
    partner.views !== null
      ? safeString(partner.views, "0")
      : "0";

  const likes = safeNumber(partner.likes, 0);

  const apiDistance = safeString(
    partner.distance,
    ""
  );

  return {
    /* Database identity */
    id: partner.id,

    secondaryId: safeString(
      partner.secondary_id,
      ""
    ),

    secondaryUuid: safeString(
      partner.secondary_uuid,
      ""
    ),

    /* Profile */
    name: safeString(
      partner.full_name,
      "Partner"
    ),

    avatar: getProfileImage(partner.profile),

    gender: safeString(partner.gender, ""),

    age,
    bio,

    /* Work */
    category: work,
    work,
    secondaryWork,
    skills,
    exp: experiences,
    teamSize: formatTeamSize(partner),
    recentlyWork,

    /* Status */
    status: partner.stutes || "",
    onlineStatus: partner.online_stutes || "",
    online,

    /* Marketplace */
    tag: online ? "Available Now" : null,
    tier: null,
    rating,
    views,
    likes,
    price: formatPrice(partner),

    mode: safeString(partner.mode, "2TW 🚲"),

    /* Distance */
    distance:
      apiDistance || "Distance unavailable",
    distanceKm: null,

    partnerLatitude: partnerCoordinates
      ? partnerCoordinates.latitude
      : null,

    partnerLongitude: partnerCoordinates
      ? partnerCoordinates.longitude
      : null,

    hasValidLocation: Boolean(partnerCoordinates),

    /* Contact */
    phoneNumber: safeString(
      partner.phone_number,
      ""
    ),

    email: safeString(partner.email_id, ""),

    /* Location */
    address: safeString(partner.address, ""),

    latitudeLongitude: location,

    /* Financial */
    wallet: safeNumber(partner.wallet, 0),
    money: safeNumber(partner.money, 0),

    working_charges: safeString(
      partner.working_charges,
      ""
    ),

    profit: safeNumber(partner.profit, 0),
    invest: safeNumber(partner.invest, 0),

    /* Dates */
    createdAt: partner.created_at || null,
    updatedAt: partner.updated_at || null,
  };
};

/* =========================================================
   SEARCH COMPONENT
========================================================= */

export default function Search() {
  const navigate = useNavigate();

  const { userId, deviceUuid } = useParams();

  const basePath =
    userId && deviceUuid
      ? `/${userId}/${deviceUuid}`
      : "";

  /* Search states */
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("search");
  const [menuOpen, setMenuOpen] = useState(false);
  const [providerData, setProviderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [sortBy, setSortBy] = useState("distance");
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [onlineOnly, setOnlineOnly] = useState(false);

  /* Live location */
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationError, setLocationError] = useState("");

  /* =======================================================
     LIVE DEVICE LOCATION
  ======================================================= */

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationLoading(false);
      setLocationError(
        "Your device/browser does not support live location."
      );
      return undefined;
    }

    let watchId = null;

    try {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const latitude = Number(
            position.coords.latitude
          );

          const longitude = Number(
            position.coords.longitude
          );

          if (
            !Number.isFinite(latitude) ||
            !Number.isFinite(longitude)
          ) {
            return;
          }

          setUserLocation({
            latitude,
            longitude,
            accuracy:
              Number(position.coords.accuracy) ||
              null,
          });

          setLocationLoading(false);
          setLocationError("");
        },
        (geoError) => {
          console.error(
            "Live Location Error:",
            geoError
          );

          setLocationLoading(false);

          switch (geoError.code) {
            case geoError.PERMISSION_DENIED:
              setLocationError(
                "Location permission denied. Please allow location access."
              );
              break;

            case geoError.POSITION_UNAVAILABLE:
              setLocationError(
                "Live location is currently unavailable."
              );
              break;

            case geoError.TIMEOUT:
              setLocationError(
                "Live location request timed out."
              );
              break;

            default:
              setLocationError(
                "Unable to get live device location."
              );
          }
        },
        {
          enableHighAccuracy: true,
          maximumAge: 5000,
          timeout: 15000,
        }
      );
    } catch (geoException) {
      console.error(
        "Geolocation Exception:",
        geoException
      );

      setLocationLoading(false);
      setLocationError(
        "Unable to start live location."
      );
    }

    return () => {
      if (
        watchId !== null &&
        navigator.geolocation
      ) {
        navigator.geolocation.clearWatch(
          watchId
        );
      }
    };
  }, []);

  /* =======================================================
     FETCH PARTNERS
  ======================================================= */

  const fetchPartners = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(
          `HTTP Error ${response.status}`
        );
      }

      const result = await response.json();

      console.log(
        "Partner API Response:",
        result
      );

      if (!result || result.success !== true) {
        throw new Error(
          result?.message ||
            "Partner data fetch failed."
        );
      }

      const partners = Array.isArray(result.data)
        ? result.data
        : [];

      const formatted = partners.map(
        formatPartner
      );

      setProviderData(formatted);
    } catch (err) {
      console.error("Partner API Error:", err);

      setProviderData([]);

      setError(
        err?.message ||
          "Server se partner data load nahi ho raha."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  /* =======================================================
     RECALCULATE DISTANCE
  ======================================================= */

  const partnersWithDistance = useMemo(() => {
    if (
      !userLocation ||
      !Number.isFinite(userLocation.latitude) ||
      !Number.isFinite(userLocation.longitude)
    ) {
      return providerData.map((partner) => ({
        ...partner,
        distanceKm: null,
        distance: "Getting location...",
      }));
    }

    return providerData.map((partner) => {
      if (
        !partner.hasValidLocation ||
        !Number.isFinite(
          partner.partnerLatitude
        ) ||
        !Number.isFinite(
          partner.partnerLongitude
        )
      ) {
        return {
          ...partner,
          distanceKm: null,
          distance: "Location unavailable",
        };
      }

      const distanceKm = calculateDistanceKm(
        userLocation.latitude,
        userLocation.longitude,
        partner.partnerLatitude,
        partner.partnerLongitude
      );

      return {
        ...partner,
        distanceKm,
        distance: formatDistance(distanceKm),
      };
    });
  }, [providerData, userLocation]);

  /* =======================================================
     CLOSE SORT WHEN CLICKING OUTSIDE
  ======================================================= */

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        !event.target.closest(".sort-wrapper")
      ) {
        setSortOpen(false);
      }
    };

    document.addEventListener(
      "click",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "click",
        handleOutsideClick
      );
    };
  }, []);

  /* =======================================================
     FILTER + SORT
  ======================================================= */

  const filteredProviders = useMemo(() => {
    const query = searchTerm
      .trim()
      .toLowerCase();

    let result = partnersWithDistance.filter(
      (item) => {
        if (onlineOnly && !item.online) {
          return false;
        }

        if (!query) return true;

        const searchableText = [
          item.name,
          item.category,
          item.work,
          item.secondaryWork,
          item.skills,
          item.bio,
          item.gender,
          item.address,
          item.recentlyWork,
          item.secondaryId,
          item.secondaryUuid,
          item.latitudeLongitude,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(query);
      }
    );

    result = [...result].sort((a, b) => {
      if (sortBy === "distance") {
        const aDistance = Number.isFinite(
          a.distanceKm
        )
          ? a.distanceKm
          : Number.POSITIVE_INFINITY;

        const bDistance = Number.isFinite(
          b.distanceKm
        )
          ? b.distanceKm
          : Number.POSITIVE_INFINITY;

        return aDistance - bDistance;
      }

      if (sortBy === "name") {
        return a.name.localeCompare(b.name);
      }

      if (sortBy === "experience") {
        const aExp = parseFloat(a.exp) || 0;
        const bExp = parseFloat(b.exp) || 0;
        return bExp - aExp;
      }

      if (sortBy === "age") {
        const aAge = parseFloat(a.age) || 0;
        const bAge = parseFloat(b.age) || 0;
        return aAge - bAge;
      }

      const aDate = a.createdAt
        ? new Date(a.createdAt).getTime()
        : 0;

      const bDate = b.createdAt
        ? new Date(b.createdAt).getTime()
        : 0;

      return bDate - aDate;
    });

    return result;
  }, [
    partnersWithDistance,
    searchTerm,
    sortBy,
    onlineOnly,
  ]);

  /* =======================================================
     MENU
  ======================================================= */

  const handleMenu = () => {
    setMenuOpen(true);
  };

  /* =======================================================
     CALL PARTNER
  ======================================================= */

  const handleCall = (phoneNumber, event) => {
    /* Stop propagation so card click doesn't fire */
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    if (!phoneNumber) {
      alert(
        "Partner phone number available nahi hai."
      );
      return;
    }

    window.location.href = `tel:${phoneNumber}`;
  };

  /* =======================================================
     OPEN PARTNER PROFILE  ← NEW
  ======================================================= */

  const handleOpenProfile = (partner) => {
    if (!partner) return;

    /* Prefer secondary_uuid (unique), fallback to secondaryId, then id */
    const partnerKey =
      partner.secondaryUuid ||
      partner.secondaryId ||
      partner.id;

    if (!partnerKey) {
      alert("Partner ID missing.");
      return;
    }

    /* Navigate to ExtentProfile with partner key in URL.
       Also pass partner object via state as a fallback
       (so ExtentProfile can render instantly while fetching). */
    navigate(
      `${basePath}/partner/${partnerKey}`,
      {
        state: { partner },
      }
    );
  };

  /* =======================================================
     NAVIGATION HANDLERS
  ======================================================= */

  const handleHomeNavigation = () => {
    setActiveTab("home");
    navigate(`${basePath}/userprofile`);
  };

  const handleSearchNavigation = () => {
    setActiveTab("search");
    navigate(`${basePath}/search`);
  };

  const handleTasksNavigation = () => {
    setActiveTab("clipboard");
    navigate(`${basePath}/tasks`);
  };

  const handleMediaNavigation = () => {
    setActiveTab("media");
    navigate(`${basePath}/media`);
  };

  const handleWalletNavigation = () => {
    setActiveTab("wallet");
    navigate(`${basePath}/wallet`);
  };

  /* =======================================================
     SORT LABEL
  ======================================================= */

  const getSortLabel = () => {
    switch (sortBy) {
      case "distance":
        return "Nearest";
      case "name":
        return "Name";
      case "experience":
        return "Experience";
      case "age":
        return "Age";
      case "newest":
      default:
        return "Newest";
    }
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className={styles.searchPageWrapper}>
      <NavSlider
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        navigate={navigate}
      />

      <div className={styles.searchDeviceCard}>
        {/* HEADER */}
        <header className={styles.searchHeader}>
          <div className={styles.brandTitle}>
            <span className={styles.brandText}>
              BroBizee
            </span>

            <div className={styles.nearMe}>
              <span>Near me</span>

              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0066cc"
                strokeWidth="2.2"
              >
                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
          </div>

          <button
            className={styles.menuIconBtn}
            aria-label="Menu"
            type="button"
            onClick={handleMenu}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0066cc"
              strokeWidth="2.5"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </header>

        {/* SEARCH TOOLBAR */}
        <div className={styles.searchToolbar}>
          <div className={styles.searchInputBox}>
            <input
              type="text"
              placeholder="Search partner, work or skill..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
              aria-label="Search partners"
            />

            {searchTerm ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchTerm("")}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: "18px",
                  color: "#666",
                  padding: "0 6px",
                }}
              >
                ×
              </button>
            ) : (
              <svg
                className={styles.micIcon}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0066cc"
                strokeWidth="2"
              >
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
              </svg>
            )}
          </div>

          {/* SORT */}
          <div
            className="sort-wrapper"
            style={{ position: "relative" }}
          >
            <button
              className={`${styles.filterChip} ${styles.sortBtn}`}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setSortOpen(
                  (previous) => !previous
                );
              }}
              aria-label="Sort partners"
            >
              <span>
                Sort: {getSortLabel()}
              </span>

              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {sortOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  minWidth: "160px",
                  padding: "7px",
                  background: "#ffffff",
                  borderRadius: "14px",
                  boxShadow:
                    "0 12px 35px rgba(0,0,0,0.15)",
                  border:
                    "1px solid rgba(0,0,0,0.08)",
                  zIndex: 100,
                }}
              >
                {[
                  ["distance", "Nearest"],
                  ["newest", "Newest"],
                  ["name", "Name"],
                  ["experience", "Experience"],
                  ["age", "Age"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setSortBy(value);
                      setSortOpen(false);
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      border: "none",
                      background:
                        sortBy === value
                          ? "#eef5ff"
                          : "transparent",
                      color:
                        sortBy === value
                          ? "#0066cc"
                          : "#222",
                      padding: "10px 12px",
                      borderRadius: "9px",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight:
                        sortBy === value ? 700 : 500,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* HISTORY */}
          <button
            className={styles.iconChip}
            aria-label="History"
            type="button"
            onClick={() => {
              alert(
                "Search history will be available soon."
              );
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#111111"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 16 14" />
            </svg>
          </button>

          {/* FILTER */}
          <button
            className={styles.iconChip}
            aria-label="Filter Options"
            type="button"
            onClick={() =>
              setFilterOpen(
                (previous) => !previous
              )
            }
            style={{ position: "relative" }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#111111"
              strokeWidth="2"
            >
              <line x1="4" y1="21" x2="4" y2="14" />
              <line x1="4" y1="10" x2="4" y2="3" />
              <line x1="12" y1="21" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12" y2="3" />
              <line x1="20" y1="21" x2="20" y2="16" />
              <line x1="20" y1="12" x2="20" y2="3" />
              <line x1="1" y1="14" x2="7" y2="14" />
              <line x1="9" y1="8" x2="15" y2="8" />
              <line x1="17" y1="16" x2="23" y2="16" />
            </svg>

            {onlineOnly && (
              <span
                style={{
                  position: "absolute",
                  top: "4px",
                  right: "4px",
                  width: "7px",
                  height: "7px",
                  borderRadius: "50%",
                  background: "#00b86b",
                  border: "2px solid white",
                }}
              />
            )}
          </button>
        </div>

        {/* LIVE LOCATION STATUS */}
        <div
          style={{
            margin: "0 14px 8px",
            padding: "7px 10px",
            borderRadius: "10px",
            background: locationLoading
              ? "#fff8e8"
              : locationError
                ? "#fff1f1"
                : "#eefaf5",
            color: locationLoading
              ? "#9a6700"
              : locationError
                ? "#c62828"
                : "#087443",
            fontSize: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <span>
            {locationLoading
              ? "📍 Getting your live location..."
              : locationError
                ? `⚠️ ${locationError}`
                : "📍 Live location active — showing nearest partners first"}
          </span>

          {userLocation &&
            Number.isFinite(userLocation.accuracy) && (
              <span
                style={{
                  whiteSpace: "nowrap",
                  fontSize: "9px",
                }}
              >
                ±
                {Math.round(userLocation.accuracy)}
                m
              </span>
            )}
        </div>

        {/* FILTER PANEL */}
        {filterOpen && (
          <div
            style={{
              margin: "0 14px 10px",
              padding: "12px 14px",
              borderRadius: "16px",
              background: "#ffffff",
              boxShadow:
                "0 8px 24px rgba(0,0,0,0.08)",
              border:
                "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <div>
                <strong style={{ fontSize: "13px" }}>
                  Availability
                </strong>

                <div
                  style={{
                    fontSize: "11px",
                    color: "#777",
                    marginTop: "3px",
                  }}
                >
                  Show only partners currently online
                </div>
              </div>

              <button
                type="button"
                onClick={() =>
                  setOnlineOnly(
                    (previous) => !previous
                  )
                }
                aria-label="Toggle online only"
                style={{
                  width: "46px",
                  height: "25px",
                  borderRadius: "20px",
                  border: "none",
                  cursor: "pointer",
                  background: onlineOnly
                    ? "#00b86b"
                    : "#d7dce2",
                  position: "relative",
                  transition: "0.2s",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: "3px",
                    left: onlineOnly
                      ? "24px"
                      : "3px",
                    width: "19px",
                    height: "19px",
                    borderRadius: "50%",
                    background: "#ffffff",
                    boxShadow:
                      "0 2px 6px rgba(0,0,0,0.2)",
                    transition: "0.2s",
                  }}
                />
              </button>
            </div>
          </div>
        )}

        {/* RESULT HEADER */}
        {!loading && !error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 16px 8px",
              gap: "10px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "#666",
              }}
            >
              <strong style={{ color: "#111" }}>
                {filteredProviders.length}
              </strong>{" "}
              partner
              {filteredProviders.length !== 1
                ? "s"
                : ""}{" "}
              found
            </div>

            <button
              type="button"
              onClick={fetchPartners}
              style={{
                border: "none",
                background: "transparent",
                color: "#0066cc",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              ↻ Refresh
            </button>
          </div>
        )}

        {/* PROVIDER LIST */}
        <main className={styles.searchScrollBody}>
          {/* LOADING */}
          {loading && (
            <div style={{ padding: "10px 14px" }}>
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  style={{
                    minHeight: "150px",
                    marginBottom: "12px",
                    borderRadius: "18px",
                    background:
                      "linear-gradient(90deg,#f1f3f5 25%,#e7eaee 50%,#f1f3f5 75%)",
                    backgroundSize: "200% 100%",
                    animation:
                      "brobizeeSkeleton 1.4s infinite",
                  }}
                />
              ))}

              <style>
                {`
                  @keyframes brobizeeSkeleton {
                    0% {
                      background-position: 200% 0;
                    }
                    100% {
                      background-position: -200% 0;
                    }
                  }
                `}
              </style>
            </div>
          )}

          {/* ERROR */}
          {!loading && error && (
            <div
              className={styles.noResults}
              style={{
                margin: "30px 15px",
                padding: "25px 18px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "42px",
                  marginBottom: "8px",
                }}
              >
                ⚠️
              </div>

              <h3>Unable to Load Partners</h3>

              <p>{error}</p>

              <button
                type="button"
                onClick={fetchPartners}
                style={{
                  marginTop: "10px",
                  border: "none",
                  background: "#0066cc",
                  color: "#ffffff",
                  padding: "10px 20px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {/* PROVIDERS */}
          {!loading &&
            !error &&
            filteredProviders.map((item) => (
              <div
                key={item.id || item.secondaryUuid}
                className={styles.providerCard}
                onClick={() => handleOpenProfile(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" ||
                    e.key === " "
                  ) {
                    e.preventDefault();
                    handleOpenProfile(item);
                  }
                }}
                style={{
                  cursor: "pointer",
                }}
              >
                {/* TOP ROW */}
                <div className={styles.cardTopRow}>
                  {/* AVATAR */}
                  <div className={styles.avatarWrapper}>
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className={styles.providerAvatar}
                      loading="lazy"
                      onError={(event) => {
                        event.currentTarget.src =
                          DEFAULT_AVATAR;
                      }}
                    />

                    {item.online && (
                      <span
                        className={
                          styles.onlineIndicator
                        }
                      />
                    )}
                  </div>

                  {/* MAIN INFO */}
                  <div className={styles.providerMainInfo}>
                    <div className={styles.badgeRow}>
                      {item.tag && (
                        <span
                          className={`${styles.chipTag} ${styles.assuredTag}`}
                        >
                          {item.tag}
                        </span>
                      )}

                      <span
                        className={`${styles.chipTag} ${styles.categoryTag}`}
                      >
                        {item.category}
                      </span>
                    </div>

                    <h3 className={styles.providerName}>
                      {item.name}
                    </h3>

                    <div className={styles.metaStats}>
                      <span>
                        👥 {item.teamSize}
                      </span>
                      <span>Exp. {item.exp}</span>
                      <span>👤 {item.age}</span>
                    </div>
                  </div>

                  {/* RIGHT INFO */}
                  <div className={styles.providerRightInfo}>
                    <div className={styles.ratingTierRow}>
                      {item.tier && (
                        <span className={styles.tierName}>
                          {item.tier}
                        </span>
                      )}

                      <div className={styles.ratingPill}>
                        <span>{item.rating}</span>
                        <span className={styles.stars}>
                          ★★★★★
                        </span>
                      </div>
                    </div>

                    <div className={styles.viewsCount}>
                      views {item.views}
                    </div>

                    <div className={styles.pricingBox}>
                      <span className={styles.clockIcon}>
                        🕒
                      </span>

                      <div className={styles.priceText}>
                        Charges starting from <br />{" "}
                        {"₹"}{" "}
                        <strong>{item.money}</strong>
                        {"-"}
                        <strong
                          className={
                            styles.workingCharges
                          }
                        >
                          {" "}
                          {item.working_charges}{" "}
                        </strong>
                      </div>
                    </div>

                    <div className={styles.modeText}>
                      Mode - {item.mode}
                    </div>

                    <div className={styles.likesCount}>
                      {item.likes}
                      <span className={styles.thumb}>
                        👍
                      </span>
                    </div>

                    {/* LIVE DISTANCE */}
                    <div
                      className={styles.distanceText}
                      style={{
                        fontWeight: 700,
                        color: Number.isFinite(
                          item.distanceKm
                        )
                          ? "#0066cc"
                          : "#888",
                      }}
                    >
                      📍{" "}
                      {Number.isFinite(item.distanceKm)
                        ? `~ ${item.distance}`
                        : item.distance}
                    </div>
                  </div>
                </div>

                {/* SKILLS */}
                <div className={styles.skillsLine}>
                  <strong>Skills - </strong>
                  {item.skills}
                </div>

                {/* BIO */}
                {item.bio && (
                  <div
                    style={{
                      marginTop: "7px",
                      fontSize: "11px",
                      lineHeight: "1.45",
                      color: "#666",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    <strong>About - </strong>
                    {item.bio}
                  </div>
                )}

                {/* LOCATION */}
                {item.address && (
                  <div
                    style={{
                      marginTop: "7px",
                      fontSize: "11px",
                      color: "#666",
                    }}
                  >
                    📍 {item.address}
                  </div>
                )}

                {/* BOTTOM ACTION */}
                <div className={styles.cardBottomRow}>
                  {item.recentlyWork ? (
                    <div
                      className={styles.recentWorkText}
                    >
                      Recently worked at -{" "}
                      <span>{item.recentlyWork}</span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#888",
                      }}
                    >
                      Professional Service
                    </div>
                  )}

                  {/* CALL NOW — stops propagation */}
                  <button
                    className={styles.callNowBtn}
                    type="button"
                    onClick={(e) =>
                      handleCall(item.phoneNumber, e)
                    }
                    disabled={!item.phoneNumber}
                    style={{
                      opacity: item.phoneNumber
                        ? 1
                        : 0.55,
                      cursor: item.phoneNumber
                        ? "pointer"
                        : "not-allowed",
                    }}
                  >
                    <span>
                      {item.phoneNumber
                        ? "Call Now"
                        : "No Number"}
                    </span>

                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.27c1.21.49 2.53.76 3.88.76a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 5a1 1 0 011-1h3.5a1 1 0 011 1c0 1.35.27 2.67.76 3.88a1 1 0 01-.27 1.11l-2.2 2.2z" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}

          {/* NO RESULTS */}
          {!loading &&
            !error &&
            filteredProviders.length === 0 && (
              <div
                className={styles.noResults}
                style={{
                  margin: "30px 15px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "46px",
                    marginBottom: "8px",
                  }}
                >
                  🔎
                </div>

                <h3>No partners found</h3>

                <p>
                  {searchTerm
                    ? `No partner found for "${searchTerm}". Try another service or skill.`
                    : onlineOnly
                      ? "No partners are currently online."
                      : "No partner data is available yet."}
                </p>

                {(searchTerm || onlineOnly) && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      setOnlineOnly(false);
                    }}
                    style={{
                      marginTop: "8px",
                      border: "none",
                      background: "#0066cc",
                      color: "#ffffff",
                      padding: "9px 17px",
                      borderRadius: "10px",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}
        </main>

        {/* BOTTOM NAVIGATION */}
<div className={styles.bottomNavWrapper}>
  <nav className={styles.bottomNav} aria-label="Bottom Navigation">
    {/* HOME */}
    <button
      type="button"
      className={`${styles.navBtn} ${
        activeTab === "home" ? styles.active : ""
      }`}
      onClick={handleHomeNavigation}
      aria-label="Home"
    >
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>

      <span className={styles.navLabel}>Home</span>
    </button>

    {/* SEARCH */}
    <button
      type="button"
      className={`${styles.navBtn} ${
        activeTab === "search" ? styles.active : ""
      }`}
      onClick={handleSearchNavigation}
      aria-label="Search"
    >
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>

      <span className={styles.navLabel}>Search</span>
    </button>

    {/* TASKS */}
    <button
      type="button"
      className={`${styles.navBtn} ${
        activeTab === "clipboard" ? styles.active : ""
      }`}
      onClick={handleTasksNavigation}
      aria-label="Tasks"
    >
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        <rect x="8" y="2" width="8" height="4" rx="1" />
        <path d="M8 11h8" />
        <path d="M8 15h5" />
      </svg>

      <span className={styles.navLabel}>Tasks</span>
    </button>

    {/* MEDIA */}
    <button
      type="button"
      className={`${styles.navBtn} ${
        activeTab === "media" ? styles.active : ""
      }`}
      onClick={handleMediaNavigation}
      aria-label="Media"
    >
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>

      <span className={styles.navLabel}>Media</span>
    </button>

    {/* WALLET */}
    <button
      type="button"
      className={`${styles.navBtn} ${
        activeTab === "wallet" ? styles.active : ""
      }`}
      onClick={handleWalletNavigation}
      aria-label="Wallet"
    >
      <svg
        width="21"
        height="21"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="2" y="6" width="20" height="12" rx="3" />
        <path d="M18 12h.01" />
      </svg>

      <span className={styles.navLabel}>Wallet</span>
    </button>
  </nav>
</div>
      </div>
    </div>
  );
}